import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon, PhotoCamera as PhotoCameraIcon } from "@mui/icons-material";
import { useMutation } from "react-query";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints, MEDIA_URL, IMAGE_URL } from "src/network/endpoints";
import toast from "react-hot-toast";
import SelectLocation from "src/@core/components/SelectLocation";

const EditGroupDialog = ({ open, onClose, conversationId, conversationInfo, onSuccess }) => {
  const [editFormData, setEditFormData] = useState({
    groupName: "",
    description: "",
    subject: "",
    isPrivate: false,
    groupProfile: null,
    latLong: null,
    address: "",
  });
  const [groupProfilePreview, setGroupProfilePreview] = useState(null);
  const [groupProfileFile, setGroupProfileFile] = useState(null);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const fileInputRef = useRef(null);

  // Initialize form data when dialog opens or conversationInfo changes
  useEffect(() => {
    if (open && conversationInfo) {
      setEditFormData({
        groupName: conversationInfo.groupName || "",
        description: conversationInfo.description || "",
        subject: conversationInfo.subject || "",
        isPrivate: conversationInfo.isPrivate || false,
        groupProfile: conversationInfo.groupProfile || null,
        latLong: conversationInfo.latLong || null,
        address: conversationInfo.address || "",
      });
      setGroupProfilePreview(
        conversationInfo.groupProfile 
          ? `${MEDIA_URL}${conversationInfo.groupProfile}`
          : null
      );
      setGroupProfileFile(null);
    }
  }, [open, conversationInfo]);

  // Handle dialog close
  const handleClose = () => {
    setGroupProfilePreview(null);
    setGroupProfileFile(null);
    setLocationDialogOpen(false);
    onClose();
  };

  // Handle location select
  const handleLocationSelect = (locationData) => {
    setEditFormData((prev) => ({
      ...prev,
      latLong: {
        lat: locationData.latitude,
        long: locationData.longitude,
      },
      address: locationData.physicalAddress || "",
    }));
    setLocationDialogOpen(false);
  };

  // Handle file change for group profile
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("please select a valid image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("image size should be less than 5MB");
        return;
      }
      setGroupProfileFile(file);
      setGroupProfilePreview(URL.createObjectURL(file));
    }
  };

  // Image upload mutation
  const uploadImageMutation = useMutation(async (imageFile) => {
    const formData = new FormData();
    formData.append("images", imageFile);
    const response = await axiosInstance.post(
      ApiEndPoints.SOCIAL.uploadMedia,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    const uploadedImageUrl = response?.data?.images?.[0];
    if (!uploadedImageUrl) {
      throw new Error("Failed to get uploaded image URL");
    }
    const relativePath = uploadedImageUrl.startsWith(MEDIA_URL)
      ? uploadedImageUrl.replace(MEDIA_URL, "")
      : uploadedImageUrl;
    return relativePath;
  });

  // Update group mutation
  const updateGroupMutation = useMutation(
    async (payload) => {
      const response = await axiosInstance.put(
        ApiEndPoints.CHAT.updateGroup(conversationId),
        payload
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success("group updated  !");
        handleClose();
        onSuccess?.(data);
      },
      onError: (error) => {
        console.error("Error updating group:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "failed to update group. please try again.";
        toast.error(errorMessage);
      },
    }
  );

  // Handle edit form submit
  const handleSubmit = async () => {
    if (!conversationInfo) return;

    try {
      let groupProfile = editFormData.groupProfile;
      
      // Upload new image if selected
      if (groupProfileFile) {
        toast.loading("uploading group profile image...", { id: "upload-image" });
        try {
          groupProfile = await uploadImageMutation.mutateAsync(groupProfileFile);
          toast.success("image uploaded  !", { id: "upload-image" });
        } catch (error) {
          toast.error("failed to upload image", { id: "upload-image" });
          return;
        }
      }

      // Build payload with only changed fields
      const payload = {};
      if (editFormData.groupName !== conversationInfo.groupName) {
        payload.groupName = editFormData.groupName.trim();
      }
      if (editFormData.description !== conversationInfo.description) {
        payload.description = editFormData.description.trim();
      }
      if (editFormData.subject !== conversationInfo.subject) {
        payload.subject = editFormData.subject.trim();
      }
      if (editFormData.isPrivate !== conversationInfo.isPrivate) {
        payload.isPrivate = editFormData.isPrivate;
      }
      if (groupProfile && groupProfile !== conversationInfo.groupProfile) {
        payload.groupProfile = groupProfile;
      }
      
      // Check if latLong has changed
      const currentLatLong = conversationInfo.latLong;
      const newLatLong = editFormData.latLong;
      const latLongChanged = 
        (!currentLatLong && newLatLong) ||
        (currentLatLong && !newLatLong) ||
        (currentLatLong && newLatLong && (
          currentLatLong.lat !== newLatLong.lat ||
          currentLatLong.long !== newLatLong.long
        ));
      
      if (latLongChanged) {
        if (newLatLong) {
          payload.latLong = {
            lat: newLatLong.lat,
            long: newLatLong.long,
          };
        } else {
          payload.latLong = null;
        }
      }
      
      // Check if address has changed
      const currentAddress = conversationInfo.address || "";
      const newAddress = editFormData.address || "";
      if (currentAddress !== newAddress) {
        payload.address = newAddress.trim() || null;
      }

      // Only send if there are changes
      if (Object.keys(payload).length > 0) {
        await updateGroupMutation.mutateAsync(payload);
      } else {
        toast.info("no changes to save");
        handleClose();
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pb: 1,
          borderBottom: "1px solid #e0e0e0",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          edit group
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {/* Group Profile Image */}
        <Box sx={{ mb: 3, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Typography variant="subtitle2" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
            group profile picture
          </Typography>
          <Box
            sx={{
              position: "relative",
              width: 120,
              height: 120,
              cursor: "pointer",
              transition: "all 0.2s ease",
              "&:hover": {
                transform: "scale(1.02)",
              },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            {groupProfilePreview ? (
              <Avatar
                src={groupProfilePreview}
                imgProps={{
                  onError: (e) => {
                    e.target.style.display = 'none';
                    setGroupProfilePreview(null);
                  }
                }}
                sx={{
                  width: 120,
                  height: 120,
                  border: "3px solid #f0f0f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  bgcolor: "#f0f0f0",
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 36, color: "#999" }} />
              </Avatar>
            ) : (
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  border: "2px dashed #d0d0d0",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#fafafa",
                  "&:hover": {
                    borderColor: "#7C3AED",
                    backgroundColor: "#f5f0ff",
                  },
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 36, color: "#999", mb: 0.5 }} />
                <Typography variant="caption" sx={{ fontSize: "0.7rem", color: "#666" }}>
                  upload
                </Typography>
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </Box>
          {groupProfilePreview && (
            <Typography
              variant="caption"
              sx={{
                color: "#7C3AED",
                mt: 1.5,
                cursor: "pointer",
                fontWeight: 500,
                "&:hover": {
                  textDecoration: "underline",
                },
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              click to change
            </Typography>
          )}
        </Box>

        {/* Group Name */}
        <TextField
          fullWidth
          label="group name"
          value={editFormData.groupName}
          onChange={(e) =>
            setEditFormData((prev) => ({ ...prev, groupName: e.target.value }))
          }
          sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
        />

        {/* Description */}
        <TextField
          fullWidth
          label="description"
          value={editFormData.description}
          onChange={(e) =>
            setEditFormData((prev) => ({ ...prev, description: e.target.value }))
          }
          multiline
          rows={3}
          sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          inputProps={{ maxLength: 500 }}
          helperText={`${editFormData.description.length}/500`}
        />

        {/* Subject */}
        <TextField
          fullWidth
          label="subject"
          value={editFormData.subject}
          onChange={(e) =>
            setEditFormData((prev) => ({ ...prev, subject: e.target.value }))
          }
          sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
          inputProps={{ maxLength: 200 }}
          helperText={`${editFormData.subject.length}/200`}
        />

        {/* Private/Public Toggle */}
        <Box
          sx={{
            p: 2,
            borderRadius: "8px",
            backgroundColor: "#f9f9f9",
            border: "1px solid #f0f0f0",
            mb: 2.5,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={editFormData.isPrivate}
                onChange={(e) =>
                  setEditFormData((prev) => ({ ...prev, isPrivate: e.target.checked }))
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#7C3AED",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#7C3AED",
                  },
                }}
              />
            }
            label={
              <Typography sx={{ fontWeight: 500, fontSize: "0.875rem" }}>
                {editFormData.isPrivate ? "private group" : "public group"}
              </Typography>
            }
          />
          <Typography
            variant="caption"
            sx={{
              ml: 4,
              display: "block",
              textTransform: "lowercase",
              color: "#666",
              fontSize: "0.75rem",
              mt: 0.5,
            }}
          >
            {editFormData.isPrivate
              ? "only invited members can join this group"
              : "anyone can find and join this group"}
          </Typography>
        </Box>

        {/* Location Selection */}
        <Box sx={{ mb: 2.5 }}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setLocationDialogOpen(true)}
            sx={{
              mb: 1,
              color: editFormData.address ? "#333" : "#999",
              borderColor: "#ddd",
              textTransform: "lowercase",
              borderRadius: "8px",
              py: 1.5,
              fontWeight: editFormData.address ? 500 : 400,
              backgroundColor: editFormData.address ? "#f9f9f9" : "transparent",
              "&:hover": {
                borderColor: "#7C3AED",
                backgroundColor: "#f5f0ff",
                color: "#7C3AED",
              },
            }}
          >
            {editFormData.address || "select location (optional)"}
          </Button>
          {editFormData.address && (
            <Typography 
              variant="caption" 
              sx={{ 
                color: "#666",
                fontSize: "0.75rem",
                display: "block",
                mt: 0.5
              }}
            >
              {editFormData.address}
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, justifyContent: "flex-end" }}>
      
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={updateGroupMutation.isLoading || uploadImageMutation.isLoading}
          startIcon={
            (updateGroupMutation.isLoading || uploadImageMutation.isLoading) && (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            )
          }
          sx={{
            textTransform: "lowercase",
            backgroundColor: "#7C3AED",
            color: "#fff",
            borderRadius: "8px",
            px: 4,
            py: 1,
            fontWeight: 600,
            "&:hover": {
              backgroundColor: "#7b1fa2",
            },
            "&:disabled": {
              backgroundColor: "#ccc",
            },
          }}
        >
          {updateGroupMutation.isLoading || uploadImageMutation.isLoading
            ? "saving..."
            : "save changes"}
        </Button>
      </DialogActions>

      {/* Location Selection Dialog */}
      <SelectLocation
        open={locationDialogOpen}
        setOpen={setLocationDialogOpen}
        onSelect={handleLocationSelect}
        initialLocation={
          editFormData.latLong
            ? {
                lat: editFormData.latLong.lat,
                lng: editFormData.latLong.long,
                physicalAddress: editFormData.address,
              }
            : null
        }
      />
    </Dialog>
  );
};

export default EditGroupDialog;

